import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  CardMedia,
  TextField,
  Box,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function ordinal(n) {
  const s = ["th","st","nd","rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function formatPostDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const month = MONTHS[d.getMonth()];
  const day = ordinal(d.getDate());
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${month} ${day} ${year} at ${hours}:${minutes} ${ampm}`;
}

export const Post = (props) => {
  const {
    content,
    photoUrls,
    author,
    date,
    postComment,
    setComment,
    comment,
    id,
    comments,
    setComments,
    authorId,
    currentUserId,
    onUpdate,
    onDelete,
  } = props;

  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(content);
  const [saving, setSaving] = useState(false);
  const isOwner = authorId && currentUserId && authorId === currentUserId;

  const cardTitle = `${author} - ${formatPostDate(date)}`;

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await onUpdate(id, editContent);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Delete this post?")) {
      await onDelete(id);
    }
  };

  const submit = (e, postId) => {
    e.preventDefault();
    const newComment = {
      id: "temp-" + Date.now(),
      postId,
      content: comment,
      authorName: "You",
      createdAt: new Date().toISOString(),
    };
    setComments((prev) => [...prev, newComment]);
    postComment(postId);
    setComment("");
  };

  return (
    <Card sx={{ marginTop: "10px" }}>
      <CardHeader
        title={cardTitle}
        action={
          isOwner && (
            <Box>
              <IconButton size="small" onClick={() => { setEditContent(content); setEditing(true); }}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleDelete}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )
        }
      />
      {photoUrls && photoUrls.map((url, i) => (
        <CardMedia
          key={i}
          sx={{ marginTop: "10px" }}
          component="img"
          image={url}
        />
      ))}
      <CardContent sx={{ display: "flex", flexDirection: "column" }}>
        {editing ? (
          saving ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "12px" }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <TextField
                multiline
                fullWidth
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                variant="outlined"
              />
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Button size="small" variant="contained" onClick={handleSaveEdit}>Save</Button>
                <Button size="small" variant="outlined" onClick={() => setEditing(false)}>Cancel</Button>
              </Box>
            </Box>
          )
        ) : (
          <Typography variant="body1">{content}</Typography>
        )}
      </CardContent>
      <CardContent>
        {comments
          .filter((x) => x.postId === id)
          .map((x) => (
            <Box key={x.id} sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{ marginTop: "1pt", marginBottom: "1pt" }}
                color="textSecondary"
                variant="caption"
              >
                {new Date(x.createdAt).toLocaleString() + " " + x.authorName + " wrote:"}
              </Typography>
              <Typography variant="body2">{x.content}</Typography>
            </Box>
          ))}
      </CardContent>
    </Card>
  );
};
